import { PRICING, PRICE_COPY } from "./pricing.js";

// Single source of truth for per-page <head> metadata (title + description).
//
// Used in two places so they never drift:
//   1. Runtime  — <RouteMeta> in main.jsx sets document.title + meta tags on
//      client-side navigation (correct browser tab per page).
//   2. Build    — scripts/prerender-routes.mjs injects the same values into the
//      static HTML written for each route, so crawlers and social scrapers (which
//      do not run JS) read unique metadata per page.
//
// THEME/METADATA ONLY — none of this changes visible page copy, structure,
// routes, quiz logic, form fields, tracking, or compliance text.

import { ARTICLES } from "./learnArticles.js";

export const SITE_ORIGIN = "https://vyvron.com";
export const OG_IMAGE = "https://vyvron.com/logo.png";
export const SITE_NAME = "Vyvron";

export const HOME_META = {
  title: "Vyvron — Private Online Weight-Management Evaluation",
  description:
    "Vyvron helps eligible patients start a private online evaluation, connect with licensed provider review, and access prescription treatment when clinically appropriate.",
};

export const NOTFOUND_META = {
  title: "Page Not Found — Vyvron",
  description:
    "The page you're looking for can't be found. Return to Vyvron to start a private online weight-management evaluation.",
};

const PRIVACY_META = {
  title: "Privacy Policy — Vyvron",
  description:
    "Read Vyvron's Privacy Policy: how we collect, use, and protect your personal and health information on our telehealth access platform.",
};

const HIPAA_META = {
  title: "Notice of Privacy Practices — Vyvron",
  description:
    "Vyvron's Notice of Privacy Practices explains how your protected health information may be used and disclosed and your rights under HIPAA.",
};

const CANCELLATION_META = {
  title: "Cancellation & Refund Policy — Vyvron",
  description:
    "Vyvron's Cancellation & Refund Policy explains how to cancel and what refunds may apply for our telehealth access services.",
};

// Keyed by route path. Alias paths (e.g. /privacy-policy) reuse the same object.
export const PAGE_META = {
  "/": HOME_META,
  "/preview": HOME_META,
  "/about": {
    title: "About Vyvron — Telehealth Access Platform",
    description:
      "Learn about Vyvron, a telehealth platform connecting eligible patients with independent licensed providers for private online weight-management care.",
  },
  "/contact": {
    title: "Contact — Vyvron",
    description:
      "Contact Vyvron with questions about private online weight-management evaluations and access to independent licensed medical providers.",
  },
  "/privacy": PRIVACY_META,
  "/privacy-policy": PRIVACY_META,
  "/terms": {
    title: "Terms of Use — Vyvron",
    description:
      "Read Vyvron's Terms of Use governing access to our telehealth platform connecting patients with independent licensed medical providers.",
  },
  "/hipaa": HIPAA_META,
  "/notice-of-privacy-practices": HIPAA_META,
  "/telehealth-consent": {
    title: "Telehealth Consent — Vyvron",
    description:
      "Vyvron's Telehealth Consent explains how online evaluations with independent licensed providers work and what you agree to before care.",
  },
  "/california-privacy": {
    title: "California Privacy Notice — Vyvron",
    description:
      "California Privacy Notice for Vyvron: your CCPA and CPRA rights over personal information collected through our telehealth access platform.",
  },
  "/bill-of-rights": {
    title: "Patient Bill of Rights — Vyvron",
    description:
      "Vyvron Patient Bill of Rights: the privacy, transparency, and respectful-care standards you can expect from our telehealth access platform.",
  },
  "/cancellation": CANCELLATION_META,
  "/refund-policy": CANCELLATION_META,
  "/programs": {
    title: "Programs — Vyvron",
    description:
      "Explore Vyvron programs: medical weight loss, microdosing, and longevity support — provider-evaluated online, with prescription treatment when clinically appropriate.",
  },
  "/shipping-policy": {
    title: "Shipping Policy — Vyvron",
    description:
      "Vyvron's Shipping Policy explains how prescription medications are shipped by state-licensed pharmacies, delivery timelines, tracking, and where we ship.",
  },
  "/accessibility": {
    title: "Accessibility Statement — Vyvron",
    description:
      "Vyvron's Accessibility Statement describes our commitment to making our telehealth platform usable for everyone, including people with disabilities.",
  },
  "/non-discrimination": {
    title: "Non-Discrimination Notice — Vyvron",
    description:
      "Vyvron's Non-Discrimination Notice: we provide access to care without discrimination and offer free language assistance services.",
  },
  "/learn": {
    title: "Learn — GLP-1 & Weight Management Guides | Vyvron",
    description:
      "Straight answers on compounded semaglutide, GLP-1 provider safety, pricing traps, and how to verify what you read online. Educational guides from Vyvron.",
  },
  // Per-guide Learn metadata is derived from src/lib/learnArticles.js below
  // (LEARN_ARTICLE_META), so retitling a guide there updates crawler metadata.
  "/start": {
    title: "GLP-1 Weight Management — Transparent Pricing | Vyvron",
    description:
      `The price you see is the price you pay. Private online evaluation and independent licensed provider review for GLP-1 weight management. ${PRICE_COPY.medicalWeightLossPricingLine}`,
  },
  "/microdose": {
    title: "Microdose Program — Limited-Time Pricing | Vyvron",
    description:
      `A guided path to cellular energy and metabolic support. Private online evaluation and independent licensed provider review. Founding price ${PRICING.microdose.founding.display} per 4 weeks.`,
  },
  "/longevity": {
    title: "Provider-Guided Longevity Program — Vyvron",
    description:
      `A guided path to NAD+ with founding-member pricing — ${PRICING.longevity.products.nadCellularRecharge.founding.display} per 4 weeks, locked for as long as you're a member. Provider-evaluated, 100% online, cancel anytime.`,
  },
};

// Per-guide Learn metadata, derived from the shared article list so titles
// and descriptions can never drift from what /learn and the homepage show.
// `metaTitle` / `metaDescription` are optional SEO-tuned variants; the plain
// article title/summary are used when absent.
export const LEARN_ARTICLE_META = Object.fromEntries(
  ARTICLES.map((a) => [
    a.to,
    {
      title: a.metaTitle || a.title,
      description: a.metaDescription || a.summary,
    },
  ]),
);

Object.assign(PAGE_META, LEARN_ARTICLE_META);

// Resolve metadata for a path, falling back to the 404 metadata for unknown routes.
export function getMeta(pathname) {
  const clean =
    pathname !== "/" && pathname.endsWith("/")
      ? pathname.slice(0, -1)
      : pathname;
  const entry = PAGE_META[clean] || NOTFOUND_META;
  return {
    title: entry.title,
    description: entry.description,
    url: clean === "/" ? SITE_ORIGIN : SITE_ORIGIN + clean,
    image: OG_IMAGE,
  };
}
